/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-04-07 10:04:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import moment from 'moment';
import { v4 } from 'uuid';

import { SubmissionError } from 'redux-form';
import { getShop, checkHasSpecialCharactersProduct, removeExtraSpace } from 'src/helpers';
import { store } from 'src/stores/store';
import {
  NUMBER_CONDITION_PER_TYPE,
  PIN_LIMIT,
  STRATEGY_FOR_ALL_COLLECT,
  STRATEGY_FOR_ALL_QUERY,
  STRATEGY_FOR_COLLECT,
  STRATEGY_FOR_QUERY,
  STRATEGY_FOR_TAB_PRODUCT
} from '../constants/merchandising-v2';

/**
 * Validate form data before saving
 *
 * @param  {object} data merchandising rule form values
 * @param {i18n} I18n
 *
 * @return {Array}              List of errors
 */
export const validateDataBeforeSave = (ruleType: string, data: Object, i18n: Object) => {
  const { name, queries, collection, strategies, queriesType = '', products } = data;
  const isAllRule = isTypeAllRule(queriesType);

  let error = [];

  // Rule name must not be empty
  if (_.isEmpty(name)) {
    error.push(
      i18n.translate('AdminHTML.MerchandisingV2.Helper.validateDataBeforeSave_Name_Empty')
    );
  } else if (/<[^ >]+>/g.test(name)) {
    // Rule name must not have any special character
    error.push(
      i18n.translate(
        'AdminHTML.MerchandisingV2.Helper.validateDataBeforeSave_Name_SpecialCharacter'
      )
    );
  }

  if (ruleType === 'search') {
    const queriesFilter = queries.filter(e => _.isEmpty(e));
    if (!isAllRule && (queries.length === 0 || queriesFilter.length > 0)) {
      error.push(
        i18n.translate('AdminHTML.MerchandisingV2.Helper.validateDataBeforeSave_Conditions_Empty')
      );
    }
  } else {
    if (!isAllRule && (!collection || _.isEmpty(collection))) {
      error.push(
        i18n.translate('AdminHTML.MerchandisingV2.Helper.validateDataBeforeSave_Collection_Empty')
      );
    }
  }

  // Strategy must be at least 1
  const { existStrategy, hasEmptyValue } = errorStrategiesRules(strategies, products);

  if (hasEmptyValue) {
    error.push(
      i18n.translate(
        'AdminHTML.MerchandisingV2.Helper.validateDataBeforeSave_ConsequencesValue_Empty'
      )
    );
  } else if (!existStrategy) {
    error.push(
      i18n.translate('AdminHTML.MerchandisingV2.Helper.validateDataBeforeSave_Consequences_Empty')
    );
  }
  // Strategy must set value

  return error;
};
/**
 *
 * @param {*} strategies
 * @returns
 */
const errorStrategiesRules = (strategies = [], products = {}) => {
  let existStrategy = false;
  let hasEmptyValue = false;
  strategies.forEach(strategy => {
    if (strategy.type === 'pin') {
      const { pinPositions = [] } = strategy;
      const existPositionSettings = pinPositions.filter(e => !_.isEmpty(e) && !_.isEmpty(e.id));
      if (existPositionSettings && existPositionSettings.length > 0) {
        existStrategy = true;
      }
    } else {
      const { attributes = [] } = strategy;
      const attributesSetting = attributes.filter(e => !_.isEmpty(e));
      attributesSetting.forEach(attributeSetting => {
        const { attribute = '', conductor = '', value = '' } = attributeSetting;

        if (
          !_.isEmpty(attribute) &&
          !_.isEmpty(conductor) &&
          (!_.isEmpty(value) || moment(value).isValid())
        ) {
          existStrategy = true;
        }
        if (
          !_.isEmpty(attribute) &&
          (!value || (['created_at', 'publish_at'].includes(attribute) && !moment(value).isValid()))
        ) {
          hasEmptyValue = true;
        }
      });
    }
  });
  if (!existStrategy) {
    existStrategy = Object.keys(products)
      .flatMap(k => products[k])
      .some(v => v);
  }
  return { existStrategy, hasEmptyValue };
};

/**
 * Initial merchandising v2 after reading from DB
 *
 * @param  {object} merchandising
 * @return {object}
 */
export const initialMerchandisingV2 = (
  merchandising,
  duplicateRule,
  enabledProductPerformanceRanking
) => {
  const initMerchandising = !_.isEmpty(merchandising) ? merchandising : duplicateRule;
  const {
    queries,
    name = '',
    strategies = [],
    ruleType = 'search',
    collection,
    effectFrom = '',
    effectTo = '',
    queriesType = 'specific',
    isUniversal,
    id,
    queryCondition = 'are',
    isAppliedSingularAndPlurals = false,
    isDisabledPerformanceRanking = false
  } = initMerchandising;

  const products = {};
  STRATEGY_FOR_TAB_PRODUCT.forEach(type => {
    products[type] = [];
  });

  const initStrategies = strategies
    .map((strategy, index) => {
      let { type = '', attributes = [] } = strategy;
      if (type === 'pin') {
        return buildInitialPinValues(strategy, index, ruleType);
      }

      const initAttributes = attributes
        .filter(attribute => attribute.attribute !== 'product_id')
        .map(attribute => buildInitialAttributeValue(attribute));

      const productIds = attributes
        .filter(attribute => attribute.attribute === 'product_id')
        .map(attribute => buildInitialAttributeValue(attribute).value);
      if (productIds.length > 0 && STRATEGY_FOR_TAB_PRODUCT.includes(type)) {
        products[type].push(...productIds);
      }
      if (initAttributes.length === 0) {
        return null;
      }
      return {
        type,
        attributes: initAttributes
      };
    })
    .filter(strategy => strategy);

  STRATEGY_FOR_TAB_PRODUCT.forEach(type => {
    const max = NUMBER_CONDITION_PER_TYPE[type] || 3;
    if (products[type].length < max) {
      for (let i = products[type].length; i < max; i++) {
        products[type].push('');
      }
    }
  });

  // Init data for merchandising setup
  const validQueries = _.filter(queries, query => query);
  let result = {
    ruleType,
    name: !_.isEmpty(merchandising) ? name : `Copy of ${name}`,
    effectFrom: effectFrom && effectFrom !== 0 ? new Date(effectFrom * 1000).toISOString() : '',
    effectTo: effectTo && effectTo !== 0 ? new Date(effectTo * 1000).toISOString() : '',
    queries: !_.isEmpty(merchandising) && !_.isEmpty(validQueries) ? validQueries : [''],
    strategies: initStrategies,
    products: products,
    queriesType,
    isUniversal,
    id,
    queryCondition,
    isAppliedSingularAndPlurals,
    isEnablePerformanceRanking: !isDisabledPerformanceRanking && enabledProductPerformanceRanking
  };

  if (!_.isEmpty(merchandising)) {
    result.collection = collection;
  }
  return result;
};

export const initialStrategyProducts = () => {
  const products = {};
  STRATEGY_FOR_TAB_PRODUCT.forEach(type => {
    products[type] = [];
    for (let i = 0; i < NUMBER_CONDITION_PER_TYPE[type]; i++) {
      products[type].push('');
    }
  });
  return products;
};

/**
 * Build merchandising before saving
 *
 * @return {object}
 * @param merchandisingRule
 * @param status
 * @param typeBuildData
 */
export const buildMerchandisingV2 = (
  merchandisingRule: Object,
  status: string,
  typeBuildData = 'save',
  applyPPR = false
) => {
  const {
    name,
    queries = null,
    collection = null,
    ruleType,
    effectFrom,
    effectTo,
    strategies,
    queriesType = 'specific',
    products,
    queryCondition,
    isAppliedSingularAndPlurals,
    isEnablePerformanceRanking
  } = merchandisingRule;
  let savedCollection = collection;
  if (collection && !_.isEmpty(collection)) {
    savedCollection = {
      value: collection.value,
      label: collection.label
    };
  }
  const strategyProducts = [];
  Object.keys(products).forEach(type => {
    products[type]
      .filter(v => v)
      .forEach(value => {
        strategyProducts.push({
          type,
          attributes: [
            {
              conductor: 'equal',
              attribute: 'product_id',
              value
            }
          ]
        });
      });
  });
  // Build Strategies
  const strategiesResult = _.map([...strategies, ...strategyProducts], strategy => {
    const { type } = strategy;
    if (type === 'pin') {
      const { pinPositions = [] } = strategy;
      const resultPinPositions = pinPositions.map((position, index) => {
        const item = _.get(position, 'id', {});
        const positionIndex = typeBuildData === 'save' ? index : index + 1;
        return !_.isEmpty(item)
          ? {
              id: item.id,
              product_id: item.product_id ? item.product_id : 0,
              position: positionIndex
            }
          : { position: positionIndex };
      });
      const existPinValue = resultPinPositions.find(e => e.id);
      if (existPinValue) {
        return { ...strategy, pinPositions: resultPinPositions };
      }
      return {};
    } else {
      const { attributes = [] } = strategy;
      const filterAttributeNotEmpty = attributes.filter(e => {
        if (!e || _.isEmpty(e)) return false;
        const { attribute = '', conductor = '', value = '' } = e;

        if (
          !_.isEmpty(attribute) &&
          !_.isEmpty(conductor) &&
          // In case value is date => isEmpty = true, so need more condition
          (!_.isEmpty(value) ||
            (typeof value === 'object' && ['created_at', 'published_at'].includes(attribute)))
        ) {
          return true;
        }
        return false;
      });
      const attributesValue = _.map(filterAttributeNotEmpty, item => {
        // Convert product attribute
        const { attribute = '', value = '', conductor = '' } = item;
        let saveAttribute = attribute;
        let saveValue = value;
        if (_.startsWith(saveAttribute, 'opt_')) {
          saveAttribute = 'product_option';
          saveValue = `${attribute.replace('opt_', '')}:${value?.value}`;
        }

        if (
          ['collection', 'vendor', 'tag', 'product_type', 'product_category'].includes(attribute)
        ) {
          saveValue = _.get(value, 'value', null);
        }
        if (saveAttribute === 'collection') {
          saveAttribute = 'collection_id';
        } else if (saveAttribute === 'inventory') {
          saveAttribute = 'available';
        }

        if (typeBuildData === 'preview' && ['created_at', 'published_at'].includes(attribute)) {
          saveValue = Date.parse(value);
        }
        let updatedAttribute = {
          attribute: saveAttribute,
          conductor: conductor
        };

        if (saveAttribute === 'product_id') {
          updatedAttribute = {
            ...updatedAttribute,
            product_id: _.get(value, 'product_id', 0),
            value: _.get(value, 'id', 0),
            variant_ids: _.get(value, 'variant_ids', [])
          };
        } else {
          updatedAttribute = {
            ...updatedAttribute,
            value: saveValue
          };
        }
        return updatedAttribute;
      });
      return { ...strategy, attributes: attributesValue };
    }
  });
  const filterStrategies = strategiesResult.filter(
    e => !(_.isEmpty(e) || (e.type !== 'pin' && _.isEmpty(_.get(e, 'attributes', []))))
  );

  const dataSettings = {
    name,
    queries: queries
      ? queries.map((e = '') => removeExtraSpace(e)).filter(e => !_.isEmpty(e))
      : null,
    collection: savedCollection,
    ruleType,
    queriesType,
    status,
    strategies: filterStrategies,
    effectFrom: effectFrom ? Math.round(new Date(effectFrom) / 1000) : 0,
    effectTo: effectTo ? Math.round(new Date(effectTo) / 1000) : 0,
    isUniversal: isTypeAllRule(queriesType),
    queryCondition,
    isAppliedSingularAndPlurals,
    isDisabledPerformanceRanking: !isEnablePerformanceRanking
  };

  // Remove PPR from Merchandising, only apply when flag on
  if (!applyPPR) {
    delete dataSettings.isDisabledPerformanceRanking;
  }
  return {
    name,
    queries: queries
      ? queries.map((e = '') => removeExtraSpace(e)).filter(e => !_.isEmpty(e))
      : null,
    collection: savedCollection,
    ruleType,
    queriesType,
    status,
    strategies: filterStrategies,
    effectFrom: effectFrom ? Math.round(new Date(effectFrom) / 1000) : 0,
    effectTo: effectTo ? Math.round(new Date(effectTo) / 1000) : 0,
    isUniversal: isTypeAllRule(queriesType),
    queryCondition,
    isAppliedSingularAndPlurals,
    isDisabledPerformanceRanking: !isEnablePerformanceRanking
  };
};

/**
 * Build merchandising for update status before saving
 *
 * @param  {object} merchandising
 * @param  {string} status
 * @return {object}
 */
export const buildDataUpdateStatusMerchandisingV2 = (merchandisingRule: Object, status: string) => {
  const {
    name,
    queries = null,
    collection = null,
    ruleType,
    effectFrom,
    effectTo,
    strategies,
    isUniversal,
    queryCondition = 'are',
    isAppliedSingularAndPlurals = false
  } = merchandisingRule;
  let savedCollection = collection;
  if (collection && !_.isEmpty(collection)) {
    savedCollection = {
      value: collection.value,
      label: collection.label
    };
  }
  // Build Strategies
  const strategiesResult = _.map(strategies, strategy => {
    const { type } = strategy;
    if (type === 'pin') {
      const { pinPositions = [] } = strategy;
      const resultPinPositions = pinPositions.map((item, index) => {
        return !_.isEmpty(item) && item.id
          ? {
              id: item.id,
              product_id: item.product_id ? item.product_id : 0,
              position: item.position
            }
          : { position: item.position };
      });

      return { ...strategy, pinPositions: resultPinPositions };
    } else {
      const { attributes } = strategy;

      const attributesValue = _.map(attributes, item => {
        // Convert product attribute
        const { attribute = '', value = '', conductor = '', product_id = 0 } = item;
        if (attribute === 'collection_id') {
          return {
            attribute,
            value,
            conductor
          };
        }
        if (attribute === 'product_id') {
          return {
            attribute,
            value,
            conductor,
            product_id
          };
        }
        return item;
      });
      return { ...strategy, attributes: attributesValue };
    }
  });

  return {
    name,
    queries: queries ? queries.map(e => e.trim()) : null,
    collection: savedCollection,
    ruleType,
    status,
    strategies: strategiesResult,
    effectFrom: effectFrom,
    effectTo: effectTo,
    isUniversal,
    queryCondition,
    isAppliedSingularAndPlurals
  };
};

/**
 * Build initial pin values
 *
 * @param pinValue
 * @param  {number} selectedIndex
 * @param ruleType
 * @return {object}
 */
export const buildInitialPinValues = (
  pinValue: Object,
  selectedIndex: number,
  ruleType: string
) => {
  const tenantApp = _.get(store.getState(), 'oauth.tenantApp', {});
  const pintLimit = tenantApp?.settings?.visualMerchandising?.settings?.ruleLimit?.pin || PIN_LIMIT;
  let initPinPositions = Array(pintLimit).fill({});
  // Add new
  if (selectedIndex === -1) {
    const pinMatchRequired = ruleType === 'search';
    return { pinPositions: initPinPositions, pinMatchRequired, type: 'pin' };
  }

  // Edit existed consequence
  const { pinPositions = [], pinMatchRequired, type } = pinValue;
  _.map(pinPositions, item => {
    const { position, id, title: label, images, price, product_id } = item;
    if (id) {
      initPinPositions[position] = { id: { id, value: id, label, images, price, product_id } };
    } else {
      initPinPositions[position] = {};
    }
  });

  return { pinPositions: initPinPositions, pinMatchRequired, type };
};

/**
 * Validate pin values
 *
 * @param  {object} data
 * @return {object}
 */
export const validatePinValues = (data: Object, i18n: Object) => {
  const { pinPositions } = data;
  let pinPositionsError = [];
  _.map(pinPositions, (item, index) => {
    const { id } = item;
    if (!_.isEmpty(id)) {
      const i = _.findIndex(
        pinPositions,
        (itemFind, idx) => !_.isEmpty(itemFind.id) && itemFind.id.value === id.value && idx < index
      );
      if (i !== -1) {
        pinPositionsError[index] = {
          id: i18n.translate('AdminHTML.MerchandisingV2.Helper.validatePinValue_Duplicate', {
            nt_position: i + 1
          })
        };
      }
    }
  });

  // Throw error
  if (!_.isEmpty(pinPositionsError)) {
    throw new SubmissionError({ pinPositions: pinPositionsError });
  }
  return true;
};

/**
 * Build initial attributes
 *
 * @param  {object} consequence
 * @return {object}
 */
const buildInitialAttributeValue = (attributeValue: Object) => {
  // Edit existed consequence
  const { attribute, value, conductor } = attributeValue;
  let initStrategy = {
    attribute,
    value,
    conductor
  };

  switch (attribute) {
    case 'available':
      initStrategy.attribute = 'inventory';
      break;
    case 'vendor':
    case 'product_type':
    case 'product_category':
    case 'tag': {
      initStrategy.value = { value, label: value };
      break;
    }

    case 'collection_id': {
      const { label } = attributeValue;
      initStrategy.attribute = 'collection';
      initStrategy.value = { value, label };
      break;
    }
    case 'created_at':
    case 'published_at': {
      initStrategy.value = new Date(value);
      break;
    }
    case 'product_id': {
      const { title, images, price, product_id, variant_ids } = attributeValue;
      initStrategy.value = { id: value, label: title, images, price, product_id, variant_ids };
      break;
    }
    // option
    case 'product_option': {
      const splitted = value.split(':');
      initStrategy.attribute = `opt_${_.get(splitted, '[0]', '')}`;
      initStrategy.value = {
        value: _.get(splitted, '[1]', ''),
        label: _.get(splitted, '[1]', '')
      };
      break;
    }
    default: {
      initStrategy.value = value;
    }
  }

  return initStrategy;
};

/**
 * Validate attribute value
 * @param  {object} data attributes data
 * @param  {i18n} I18n
 *
 * @return {boolean}              true | false
 */
export const validateAttributeValues = (data: Object, i18n: Object) => {
  const { attributes: attributesData = [{}] } = data;
  let attributesError = [];
  _.map(attributesData, (item, index) => {
    const { attribute, conductor } = item;
    if (_.isEmpty(attribute)) {
      attributesError[index] = {
        attribute: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
      };
    } else if (_.isEmpty(conductor)) {
      attributesError[index] = {
        conductor: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
      };
    } else {
      switch (attribute) {
        case 'inventory': {
          break;
        }
        case 'title': {
          const { valueTextField } = item;
          if (_.isEmpty(valueTextField)) {
            attributesError[index] = {
              valueTextField: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
            };
          } else {
            if (checkHasSpecialCharactersProduct(valueTextField)) {
              attributesError[index] = {
                valueTextField: i18n.translate(
                  'AdminHTML.Helpers.FormValidationHelper.nonSpecialCharacters'
                )
              };
            }
          }

          break;
        }
        case 'price': {
          const { valueTextField } = item;

          if (_.isEmpty(valueTextField)) {
            attributesError[index] = {
              valueTextField: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
            };
          } else {
            if (_.isNaN(parseFloat(valueTextField))) {
              attributesError[index] = {
                valueTextField: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isNumber')
              };
            } else if (parseFloat(valueTextField) < 0) {
              attributesError[index] = {
                valueTextField: i18n.translate('AdminHTML.Helpers.FormValidationHelper.minValue', {
                  nt_number: 0
                })
              };
            }
          }

          break;
        }
        case 'vendor':
        case 'product_type':
        case 'tags':
        case 'collection': {
          const { valueSelect } = item;
          if (_.isEmpty(valueSelect)) {
            attributesError[index] = {
              valueSelect: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
            };
          }
          break;
        }
        case 'created_at':
        case 'published_at': {
          const { valueDate } = item;

          if (!(valueDate instanceof Date)) {
            attributesError[index] = {
              valueDate: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
            };
          }
          break;
        }
        case 'product_id': {
          const { valueSelectProduct } = item;
          if (_.isEmpty(valueSelectProduct)) {
            attributesError[index] = {
              valueSelectProduct: i18n.translate(
                'AdminHTML.Helpers.FormValidationHelper.isRequired'
              )
            };
          }
          break;
        }
        // option
        default: {
          const { valueSelect } = item;
          if (_.isEmpty(valueSelect)) {
            attributesError[index] = {
              valueSelect: i18n.translate('AdminHTML.Helpers.FormValidationHelper.isRequired')
            };
          }
        }
      }
    }
    return '';
  });

  // Throw error
  if (!_.isEmpty(attributesError)) {
    throw new SubmissionError({ attributes: attributesError });
  }
  return true;
};

/**
 * Build attribute values
 *
 * @param  {object} data
 * @return {object}
 */
export const buildAttributeValues = (data: Object, type) => {
  const { attributes: attributesData = [] } = data;
  let attributes = [];
  _.map(attributesData, item => {
    const { attribute, conductor } = item;
    switch (attribute) {
      case 'inventory': {
        attributes.push({ attribute, conductor });
        break;
      }
      case 'title':
      case 'price': {
        const { valueTextField } = item;
        attributes.push({ attribute, conductor, value: valueTextField });
        break;
      }
      case 'vendor':
      case 'product_type':
      case 'tags':
      case 'collection': {
        const { valueSelect } = item;
        const { value, label } = valueSelect;
        attributes.push({ attribute, conductor, value, label });
        break;
      }
      case 'created_at':
      case 'published_at': {
        const { valueDate } = item;
        attributes.push({ attribute, conductor, value: valueDate.toISOString() });
        break;
      }
      case 'product_id': {
        const { valueSelectProduct } = item;
        const { value, label, images, price, productId } = valueSelectProduct;
        attributes.push({ attribute, conductor, value, label, images, price, productId });
        break;
      }
      // option
      default: {
        const { valueSelect } = item;
        const { value, label } = valueSelect;
        attributes.push({ attribute, conductor, value, label });
      }
    }
  });

  return { attributes, type };
};

export const removeEmptyStrategy = (strategies = []) => {
  const strategiesList = strategies.map((strategy, index) => {
    if (strategy.type === 'pin') {
      const { pinPositions = [] } = strategy;
      const pinPositionsFilter = pinPositions.filter(
        e => e && !_.isEmpty(e) && e.id && !_.isEmpty(e.id)
      );
      if (pinPositionsFilter.length > 0) {
        return strategy;
      }
      return {};
    } else {
      const { attributes, type } = strategy;
      const filterAttributesNotEmpty = attributes.filter(e => {
        if (!e || _.isEmpty(e)) return false;
        const { attribute = '', conductor = '', value = '' } = e;

        if (
          !_.isEmpty(attribute) &&
          !_.isEmpty(conductor) &&
          // In case value is date => isEmpty = true, so need more condition
          (!_.isEmpty(value) ||
            (moment(value).isValid() && ['created_at', 'published_at'].includes(attribute)))
        ) {
          return true;
        }
        return false;
      });
      if (!filterAttributesNotEmpty.length) return {};
      return {
        type,
        attributes: filterAttributesNotEmpty
      };
    }
  });
  return strategiesList.filter(e => e && !_.isEmpty(e));
};

export const hasProductInRule = products => {
  return Object.keys(products).find(type => products[type].find(v => v && !_.isEmpty(v)));
};

export const capitalize = (s: string) => {
  return s[0]?.toUpperCase() + s?.slice(1);
};

/**
 *
 * @param {*} merchandising
 * @param {*} options
 *            {
 *              page: number,
 *              limit: number,
 *              onlyApplyFilterCondition: boolean // Get products based on filter condition (exclude search query and consequences)
 *            }
 * @returns
 */
export const buildRequestBodyPreview = (merchandising, options) => {
  const { page = 1, limit = 10, variantsDisplay = {}, query, ignore_performance_ranking } = options;
  const { queries = [], strategies = [], collection = {}, ruleType, queriesType } = merchandising;
  const requestBody = {
    _: 'pf',
    shop: getShop(),
    page,
    limit,
    sort: 'relevance',
    rid: v4(),
    sid: v4(),
    t: Date.now(),
    event_type: 'merchandising',
    ignore_performance_ranking,
    collection_scope: collection && !_.isEmpty(collection) ? _.get(collection, 'value', 0) : 0,
    variant_available: false,
    product_available: false
  };

  const variantsDisplayEnable = _.get(variantsDisplay, 'enable', false);

  const consequences = [];
  for (const strategy of strategies) {
    const { type, attributes = [], pinPositions = [], pinMatchRequired } = strategy;

    if (type === 'pin') {
      const currentPosition = pinPositions.filter(pos => pos.id);
      if (!_.isEmpty(currentPosition)) {
        const pin_positions = pinPositions.map(pos => {
          // current id = hasVariant ? variant_id : product_id
          //         product_id = hasVariant ? product_id: 0
          // If enable variant display => variant_id = id || product_id
          // If disable variant display => variant_id = id || 0
          if (pos.id) {
            return {
              product_id: pos.product_id || pos.id,
              variant_id: pos.product_id || variantsDisplayEnable ? pos.id : 0,
              position: pos.position
            };
          }
          return { position: pos.position };
        });
        consequences.push({
          type: 'pin',
          pin_match_required: pinMatchRequired,
          pin_positions
        });
      }
    }

    const validAttributes = attributes.filter(attr => attr.value);
    if (_.isEmpty(validAttributes)) {
      continue;
    }

    const additionalConsequences = [];
    const params = validAttributes.flatMap(attr => {
      if (attr.attribute === 'product_id') {
        if (isCollectionAllType(ruleType, queriesType)) {
          // include variants to get preview if user choose all collection
          additionalConsequences.push(
            ...(attr.variant_ids || []).map(variantId => ({
              type,
              attributes: [
                {
                  attribute: attr.attribute,
                  product_id: variantId,
                  variant_id: variantId,
                  conductor: attr.conductor
                }
              ]
            }))
          );
          return [
            {
              attribute: attr.attribute,
              product_id: attr.product_id || attr.value,
              variant_id: attr.product_id || attr.value,
              conductor: attr.conductor
            }
          ];
        } else {
          return [
            {
              attribute: attr.attribute,
              product_id: attr.product_id || attr.value,
              variant_id: attr.product_id || variantsDisplayEnable ? attr.value : 0,
              conductor: attr.conductor
            }
          ];
        }
      } else if (attr.attribute === 'available') {
        if (attr.value === 'in_stock') {
          return [
            {
              attribute: attr.attribute,
              value: true,
              conductor: attr.value
            }
          ];
        } else {
          return [
            {
              attribute: attr.attribute,
              value: false,
              conductor: attr.value
            }
          ];
        }
      } else {
        return [
          {
            attribute: attr.attribute,
            value: attr.value,
            conductor: attr.conductor
          }
        ];
      }
    });

    consequences.push({
      type,
      attributes: params
    });
    consequences.push(...additionalConsequences);
  }
  requestBody.merchandising_rule = {
    consequences
  };

  // Get search condition
  if (queries && queries.length > 0) {
    requestBody.q = query
      ? query
      : queries
          .map(q => _.trim(q))
          .filter(q => q)
          .pop();
  }

  if (ruleType === 'search') {
    requestBody.requestFrom = 'commerceSearch';
  }

  return requestBody;
};

/**
 * Get excluded product list
 *
 * @param  {array} attributes
 * @param  {array} consequences
 * @return {array}
 */

export const getExcludedProductList = (strategies, strategyProducts) => {
  let excludedList = [];

  // get all product from form merchandising rule values
  strategies.forEach(e => {
    if (e.type === 'pin') {
      excludedList = excludedList.concat(
        e.pinPositions.map(e => {
          if (!_.isEmpty(_.get(e, 'id', {}))) {
            return e.id;
          }
          return null;
        })
      );
    } else {
      excludedList = excludedList.concat(
        e.attributes?.map(e => {
          if (e.attribute === 'product_id') {
            return e.value;
          }
          return null;
        })
      );
    }
  });
  excludedList = excludedList.concat(
    Object.keys(strategyProducts).flatMap(k => strategyProducts[k])
  );

  return excludedList.filter(e => e);
};

/**
 * Get rule type from search params
 * @param {*} ruleType
 * @param {*} ruleTypeDuplicate
 * @param {*} ruleTypeParam
 * @returns
 */
export const getRuleType = (ruleType, ruleTypeDuplicate, ruleTypeParam) => {
  // Return ruleType for data from edit page
  if (ruleType) return ruleType;
  if (ruleTypeDuplicate) return ruleTypeDuplicate;
  if (ruleTypeParam) return ruleTypeParam;
  return 'search';
};

export const isTypeAllRule = queriesType => queriesType && queriesType === 'all';

export const getStrategyField = (ruleType, queriesType, page) => {
  if (ruleType === 'search') {
    if (queriesType === 'all') {
      return STRATEGY_FOR_ALL_QUERY;
    } else {
      return STRATEGY_FOR_QUERY;
    }
  } else {
    if (queriesType === 'all') {
      // show in page management list rules
      if (page === 'list') {
        const strategiesTypes = [...STRATEGY_FOR_ALL_COLLECT];
        return strategiesTypes;
      }
      return STRATEGY_FOR_ALL_COLLECT;
    } else {
      return STRATEGY_FOR_COLLECT;
    }
  }
};

export const isCollectionAllType = (ruleType, queriesType) => {
  return ruleType === 'collection' && isTypeAllRule(queriesType);
};
